import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, ShieldCheck, Stethoscope, User, Activity, FlaskConical, ScanLine,
  Pill, Salad, HeartPulse, Ambulance, Cross, Building2, Users, Accessibility,
} from "lucide-react";
import { useCareRecipientStore } from "@/store/careRecipientStore";

const SECTIONS = [
  {
    heading: "Medical at Home", emoji: "🏠",
    items: [
      { Icon: Stethoscope, title: "Nursing", color: "#2563EB", box: "#DBEAFE", to: "/senior-care/book-nurse", badge: "Included in plan" },
      { Icon: User, title: "Doctor Visit", color: "#0891B2", box: "#CFFAFE", to: "/doctors", badge: "Included in plan" },
      { Icon: Activity, title: "Physiotherapy", color: "#7C3AED", box: "#EDE9FE", to: "/at-home-care/physiotherapy" },
    ],
  },
  {
    heading: "Diagnostics & Medicines", emoji: "🧪",
    items: [
      { Icon: FlaskConical, title: "Path Lab", color: "#DC2626", box: "#FEE2E2", to: "/lab-booking" },
      { Icon: ScanLine, title: "Diagnostic Centre", color: "#2563EB", box: "#DBEAFE", to: "/find-labs" },
      { Icon: Pill, title: "Pharmacy", color: "#0EA5E9", box: "#E0F2FE", soon: true },
    ],
  },
  {
    heading: "Specialist Care", emoji: "🌿",
    items: [
      { Icon: Salad, title: "Nutritionist", color: "#16A34A", box: "#DCFCE7", to: "/doctors" },
      { Icon: HeartPulse, title: "Mental Wellness", color: "#7C3AED", box: "#EDE9FE", to: "/doctors" },
    ],
  },
  {
    heading: "Emergency & Hospital Support", emoji: "🚨",
    items: [
      { Icon: Ambulance, title: "Ambulance", color: "#DC2626", box: "#FEE2E2", to: "/ambulance" },
      { Icon: Cross, title: "Paramedic", color: "#2563EB", box: "#DBEAFE", to: "/paramedic" },
      { Icon: Building2, title: "Hospital Appointment", color: "#0891B2", box: "#CFFAFE", to: "/find-care" },
    ],
  },
  {
    heading: "Wellness & Rehabilitation", emoji: "🧘",
    items: [
      { Icon: Users, title: "Health Camps", color: "#2563EB", box: "#DBEAFE", soon: true },
      { Icon: Accessibility, title: "Rehab Programs", color: "#7C3AED", box: "#EDE9FE", to: "/at-home-care/physiotherapy" },
    ],
  },
];

export default function SeniorCareServices() {
  const navigate = useNavigate();
  const { recipient, mode } = useCareRecipientStore();

  const handle = (s) => {
    if (s.soon) { toast("🚧 Coming Soon", { description: `"${s.title}" is on our roadmap.` }); return; }
    if (s.to) navigate(s.to);
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <button onClick={() => navigate(-1)} data-testid="services-back" className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div>
        <h1 className="text-2xl font-extrabold" style={{ color: "#1E3A8A" }}>Book care when you need it</h1>
        <p className="mt-1 text-muted-foreground">On-demand services from our verified healthcare network.</p>
      </div>

      {mode === "guardian" && recipient && (
        <div className="flex items-center gap-3 rounded-2xl p-3" style={{ backgroundColor: "#FFF7ED", border: "1px solid #FED7AA" }} data-testid="services-recipient">
          <div className="h-9 w-9 rounded-full flex items-center justify-center font-bold" style={{ backgroundColor: "#FFEDD5", color: "#EA580C" }}>{recipient.name?.charAt(0)?.toUpperCase()}</div>
          <div className="text-sm"><span className="text-muted-foreground">Booking for </span><span className="font-bold" style={{ color: "#7C2D12" }}>{recipient.name}</span></div>
        </div>
      )}

      {SECTIONS.map((sec) => (
        <section key={sec.heading}>
          <h2 className="text-lg font-bold mb-3">{sec.emoji}  {sec.heading}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {sec.items.map((s) => (
              <button
                key={s.title}
                onClick={() => handle(s)}
                data-testid={`svc-${s.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="rounded-2xl border border-border bg-card p-4 text-left hover:shadow-md hover:-translate-y-0.5 transition-all min-h-[128px] flex flex-col"
              >
                <div className="h-12 w-12 rounded-2xl flex items-center justify-center mb-2.5" style={{ backgroundColor: s.box }}>
                  <s.Icon className="h-6 w-6" style={{ color: s.color }} />
                </div>
                <p className="font-bold text-sm leading-tight">{s.title}</p>
                {s.badge ? (
                  <span className="mt-2 inline-block self-start rounded-lg px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: "#DCFCE7", color: "#15803D" }}>{s.badge}</span>
                ) : s.soon ? (
                  <span className="mt-2 text-[11px] font-bold" style={{ color: "#F59E0B" }}>Coming soon</span>
                ) : (
                  <span className="mt-2 text-xs text-muted-foreground">From ₹ ___</span>
                )}
              </button>
            ))}
          </div>
        </section>
      ))}

      <div className="flex items-center gap-2 text-sm text-muted-foreground pt-2">
        <ShieldCheck className="h-4 w-4" style={{ color: "#16A34A" }} />
        All services powered by verified Trata Health providers &amp; hospitals
      </div>
    </div>
  );
}
